import React, { useState, useEffect } from 'react';

export interface UserDemographicsData {
  age?: number;
  sex?: 'male' | 'female' | 'other';
  isPregnant?: boolean;
}

interface UserDemographicsProps {
  onUpdate: (demographics: UserDemographicsData) => void;
  initialData?: UserDemographicsData;
}

export const UserDemographics: React.FC<UserDemographicsProps> = ({ onUpdate, initialData }) => {
  const [demographics, setDemographics] = useState<UserDemographicsData>(initialData || {});
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    // Load from localStorage
    const saved = localStorage.getItem('userDemographics');
    if (saved) {
      const parsed = JSON.parse(saved);
      setDemographics(parsed);
      onUpdate(parsed);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = () => {
    localStorage.setItem('userDemographics', JSON.stringify(demographics));
    onUpdate(demographics);
    setIsEditing(false);
  };

  const handleUpdate = (field: keyof UserDemographicsData, value: any) => {
    const updated = { ...demographics, [field]: value };
    if (field === 'sex' && value !== 'female') {
      delete updated.isPregnant;
    }
    setDemographics(updated);
  };

  if (!isEditing && (!demographics.age || !demographics.sex)) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
        <div className="flex items-start gap-2">
          <svg className="w-5 h-5 text-yellow-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div className="flex-1">
            <h3 className="text-sm font-medium text-yellow-900">Improve reference range accuracy</h3>
            <p className="text-sm text-yellow-700 mt-1">
              Add your age and sex to get personalized reference ranges for your lab results.
            </p>
            <button
              onClick={() => setIsEditing(true)}
              className="mt-2 text-sm font-medium text-yellow-900 hover:text-yellow-800"
            >
              Add demographics →
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (isEditing) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4">
        <h3 className="text-sm font-medium text-gray-900 mb-3">Your Demographics</h3>
        
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Age</label>
            <input
              type="number"
              min="1"
              max="120"
              value={demographics.age || ''}
              onChange={(e) => handleUpdate('age', parseInt(e.target.value) || undefined)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter your age"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Sex</label>
            <select
              value={demographics.sex || ''}
              onChange={(e) => handleUpdate('sex', e.target.value || undefined)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Select sex</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="other">Other</option>
            </select>
          </div>

          {demographics.sex === 'female' && (
            <div>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={demographics.isPregnant || false}
                  onChange={(e) => handleUpdate('isPregnant', e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">Currently pregnant</span>
              </label>
            </div>
          )}
        </div>

        <div className="flex gap-2 mt-4">
          <button
            onClick={handleSave}
            disabled={!demographics.age || !demographics.sex}
            className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            Save
          </button>
          <button
            onClick={() => {
              setIsEditing(false);
              setDemographics(initialData || {});
            }}
            className="px-4 py-2 bg-white text-gray-700 text-sm font-medium rounded-md border border-gray-300 hover:bg-gray-50"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-4 flex items-center justify-between">
      <div className="flex items-center gap-4 text-sm">
        <div>
          <span className="text-gray-500">Age:</span>{' '}
          <span className="font-medium text-gray-900">{demographics.age}</span>
        </div>
        <div>
          <span className="text-gray-500">Sex:</span>{' '}
          <span className="font-medium text-gray-900 capitalize">{demographics.sex}</span>
        </div>
        {demographics.isPregnant && (
          <div className="text-purple-600 font-medium">Pregnant</div>
        )}
      </div>
      <button
        onClick={() => setIsEditing(true)}
        className="text-sm text-gray-600 hover:text-gray-900"
      >
        Edit
      </button>
    </div>
  );
};